import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Spinner, Input, Button, Tooltip } from "@heroui/react";
import { Typography, UniversalTable } from "../../../../../components";
import { FilterSelect } from "../../../../../components/ui";
import { staffApi, CPLOrder } from "../../../../../api/staff";
import { useTranslations } from "../../../../../contexts/translation.context";
import {
  EmptyContentIc,
  SearchInputIc,
  DangerIc,
  ResetFiltersIc,
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  GlobalIc,
  IcReddit,
  IcAnotherSoc,
  IcTg,
  IcTikTok,
  IcYoutube
} from "../../../../../components/icons";
import { transformCPLOrderToOrderData, OrderData } from "./CPLCampaigns/utils";
import OrderIdCell from "./CPLCampaigns/OrderIdCell";
import AccountCell from "./CPLCampaigns/AccountCell";
import OfferCell from "./CPLCampaigns/OfferCell";
import CampaignCell from "./CPLCampaigns/CampaignCell";
import StatusCell from "./CPLCampaigns/StatusCell";
import DateCell from "./CPLCampaigns/DateCell";

interface CPLCampaignsTableProps {
  creatorId: string;
}

interface CPLFilters {
  search: string;
  status: string;
  sort: string;
}

const DEFAULT_FILTERS: CPLFilters = {
  search: '',
  status: 'all',
  sort: 'all',
};

const SEARCH_DEBOUNCE_DELAY = 500;

/**
 * CPLCampaignsTable Component
 *
 * Таблица CPL кампаний криейтора (для вкладки "Статистика").
 * Полностью основана на структуре из onlyu_front/src/client/subscribers/Orders
 */
const CPLCampaignsTable: React.FC<CPLCampaignsTableProps> = ({ creatorId }) => {
  const { t } = useTranslations();
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<CPLFilters>(DEFAULT_FILTERS);
  const [localSearch, setLocalSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Sync local search with filters
  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  // Get filter options with translations
  const getStatusOptions = useMemo(() => [
    { key: 'all', label: t('orders.filters.status.label') || 'Статус' },
    { key: 'in_progress', label: t('orders.filters.status.inProgress') || 'В работе' },
    { key: 'waiting', label: t('orders.filters.status.pending') || 'В ожидании' },
    { key: 'completed', label: t('orders.filters.status.completed') || 'Завершён' },
    { key: 'canceled', label: t('orders.filters.status.cancelled') || 'Отменён' },
  ], [t]);

  const getSortOptions = useMemo(() => [
    { key: 'all', label: t('orders.filters.sort.label') || 'Сортировка' },
    { key: '-created_at', label: t('orders.filters.sort.orderDate') || 'Дата заказа (убыв.)' },
    { key: 'created_at', label: t('orders.filters.sort.orderDate') || 'Дата заказа (возр.)' },
  ], [t]);

  const apiParams = useMemo(() => {
    const params: any = {
      page: currentPage,
      page_size: pageSize,
    };

    if (filters.search) {
      params.id = filters.search;
    }

    if (filters.status !== 'all') {
      params.status = filters.status;
    }

    if (filters.sort !== 'all') {
      params.sort_by = filters.sort;
    }

    return params;
  }, [currentPage, pageSize, filters]);

  // Функция получения иконки платформы
  const getPlatformIcon = useCallback((source: string) => {
    const iconSize = 14;
    switch (source) {
      case 'only_fans':
        return <OnlyFansIc size={iconSize} fill="#A8A8B0" />;
      case 'instagram':
        return <InstagramIc size={iconSize} fill="#A8A8B0" />;
      case 'reddit':
        return <IcReddit size={iconSize} />;
      case 'twitter':
        return <TwitterIc size={iconSize} fill="#A8A8B0" />;
      case 'tiktok':
        return <IcTikTok size={iconSize} />;
      case 'google':
        return <GoogleIc size={iconSize} fill="#A8A8B0" />;
      case 'telegram':
        return <IcTg size={iconSize} />;
      case 'youtube':
        return <IcYoutube size={iconSize} />;
      case 'another':
        return <IcAnotherSoc size={iconSize} />;
      default:
        return <GlobalIc size={iconSize} />;
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await staffApi.getCreatorCPLOrders(creatorId, apiParams);

      // Transform CPLOrder to OrderData
      const transformedOrders = response.objects.map((cplOrder: CPLOrder) =>
        transformCPLOrderToOrderData(cplOrder, getPlatformIcon)
      );

      setOrders(transformedOrders);
      setTotalCount(response.count);
    } catch (error) {
      console.error("Error fetching CPL orders:", error);
      setOrders([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [creatorId, apiParams, getPlatformIcon]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleFilterChange = useCallback((key: keyof CPLFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || filters.status !== 'all' || filters.sort !== 'all';
  }, [filters]);

  // Колонки таблицы
  const columns = useMemo(() => [
    {
      key: "id",
      name: t('orders.table.columns.id') || 'ID',
      sortable: false
    },
    {
      key: "account",
      name: t('orders.table.columns.account') || 'Аккаунт',
      sortable: false
    },
    {
      key: "offer",
      name: t('orders.table.columns.offer') || 'Оффер',
      sortable: false
    },
    {
      key: "campaign",
      name: t('orders.table.columns.campaign') || 'Кампания',
      sortable: false
    },
    {
      key: "status",
      name: t('orders.table.columns.status') || 'Статус',
      sortable: false
    },
    {
      key: "startDate",
      name: t('orders.table.columns.startDate') || 'Дата старта',
      sortable: false
    }
  ], [t]);

  // Cell renderer
  const renderCell = useCallback((order: OrderData, columnKey: string) => {
    switch (columnKey) {
      case "id":
        return <OrderIdCell order={order} />;
      case "account":
        return <AccountCell order={order} />;
      case "offer":
        return <OfferCell order={order} />;
      case "campaign":
        return <CampaignCell order={order} />;
      case "status":
        return <StatusCell order={order} />;
      case "startDate":
        return <DateCell order={order} />;
      default:
        return null;
    }
  }, []);

  const emptyContent = (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <EmptyContentIc />
        <div className="flex flex-col gap-[6px]">
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {t('orders.table.empty.title') || 'Нет CPL кампаний'}
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {t('orders.table.empty.description') || 'У этого криейтора пока нет CPL кампаний'}
          </Typography>
        </div>
      </div>
    </div>
  );

  // Loading state
  if (isLoading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Typography variant="h6" className="flex-shrink-0">
        Заказы
      </Typography>
      {/* Filters Section */}
      <div className="pb-[24px] flex items-center gap-[16px] flex-wrap justify-between w-full flex-shrink-0">
        <div className="flex items-center gap-[16px] flex-wrap justify-start">
          <Input
            type="text"
            placeholder="ID"
            size="sm"
            value={localSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalSearch(e.target.value)}
            endContent={<SearchInputIc />}
            classNames={{
              mainWrapper: "w-[273px]",
              base: "w-[273px]",
              input: "text-sm",
              inputWrapper: "rounded-lg h-[36px] w-[273px]",
            }}
          />

          <FilterSelect
            placeholder={t('orders.filters.status.placeholder') || 'Статус'}
            options={getStatusOptions}
            selectedKey={filters.status}
            variant="campaign"
            onSelectionChange={(value) => handleFilterChange('status', value)}
            startContent={<DangerIc size={20} />}
            autoWidth={true}
          />
          {hasActiveFilters && (
            <Tooltip content={t('orders.filters.resetTooltip') || 'Сбросить фильтры'} placement="top">
              <Button
                size="sm"
                className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
                onPress={handleResetFilters}
              >
                <ResetFiltersIc />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-hidden">
        <UniversalTable
          data={orders}
          columns={columns}
          renderCell={renderCell}
          emptyContent={emptyContent}
          pagination={{
            count: totalCount,
            page_size: pageSize,
            page: currentPage,
          }}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          currentPage={currentPage}
          currentPageSize={pageSize}
        />
      </div>
    </div>
  );
};

export default CPLCampaignsTable;
